import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
    ChevronLeft,
    ChevronRight,
    Shield,
    Unlock,
    Trash2,
    Calendar as CalendarIcon,
    Clock,
    User,
    Phone,
    CheckCircle,
    XCircle,
    Plus,
    X,
    MoreHorizontal
} from 'lucide-react';

import BookingDetailsModal from './BookingDetailsModal';

const API_BASE_URL = '/api';

// Mirrors utils/bookingAvailability.js on the server: confirmed/completed always
// occupy their slot; pending/failed only within this window of being created (a
// customer mid-payment or retrying after a failure). Without the time check here,
// this calendar disagrees with what /api/check-availability shows customers —
// which is exactly the bug that made a failed-payment slot look "free" in the admin
// dashboard while the website still held it.
const ACTIVE_HOLD_MINUTES = 60;
const isBookingOccupyingSlot = (b) => {
    if (b.payment_status === 'confirmed' || b.payment_status === 'completed') return true;
    if (b.payment_status === 'pending' || b.payment_status === 'failed') {
        return moment(b.created_at).isAfter(moment().subtract(ACTIVE_HOLD_MINUTES, 'minutes'));
    }
    return false;
};

const CalendarManager = ({ isAuthenticated }) => {
    const [currentDate, setCurrentDate] = useState(moment());
    const [selectedDate, setSelectedDate] = useState(moment());
    const [bookings, setBookings] = useState([]);
    const [blockedSlots, setBlockedSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);
    // A failed fetch leaves bookings/blockedSlots empty, which renders as "every slot
    // free" — indistinguishable from a genuinely empty month and an invitation to
    // double-book. Track the failure so the UI can say so instead of lying.
    const [loadError, setLoadError] = useState(false);

    // Stats for the selected day
    const [dayStats, setDayStats] = useState({
        totalSlots: 6,
        available: 0,
        booked: 0,
        blocked: 0
    });

    // Slots Definition
    const slots = [
        { id: 'A1', label: '06:30 AM', duration: '3hr', type: 'Morning' },
        { id: 'A2', label: '11:00 AM', duration: '3hr', type: 'Mid-Day' },
        { id: 'A3', label: '03:30 PM', duration: '3hr', type: 'Afternoon' },
        { id: 'B1', label: '06:00 AM', duration: '6hr', type: 'Half Day' },
        { id: 'B2', label: '01:00 PM', duration: '6hr', type: 'Half Day' },
        { id: 'C1', label: '06:00 AM', duration: '12hr', type: 'Full Day' }
    ];

    // Fetch data on month change
    useEffect(() => {
        if (isAuthenticated) {
            fetchMonthData();
        }
    }, [isAuthenticated, currentDate]);

    // Update stats when selected date changes
    useEffect(() => {
        calculateDayStats();
    }, [selectedDate, bookings, blockedSlots]);

    const fetchMonthData = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const token = localStorage.getItem('admin_token');
            const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DD');
            const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DD');

            // Fetch bookings
            const bookingsRes = await axios.get(`${API_BASE_URL}/admin/bookings`, {
                params: {
                    start_date: startOfMonth,
                    end_date: endOfMonth,
                    limit: 1000
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch blocked slots
            const blockedRes = await axios.get(`${API_BASE_URL}/admin/blocked-slots`, {
                params: {
                    start_date: startOfMonth,
                    end_date: endOfMonth,
                    limit: 1000
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!bookingsRes.data.success || !blockedRes.data.success) {
                throw new Error('Calendar data request did not succeed');
            }
            setBookings(bookingsRes.data.bookings);
            setBlockedSlots(blockedRes.data.blocked_slots);
        } catch (error) {
            console.error('Error fetching calendar data:', error);
            // Clear rather than keep stale rows, and flag it — an availability view that
            // is wrong in the "free" direction is worse than one that refuses to answer.
            setBookings([]);
            setBlockedSlots([]);
            setLoadError(true);
            toast.error('Failed to load calendar data');
        } finally {
            setLoading(false);
        }
    };

    const calculateDayStats = () => {
        const dateStr = selectedDate.format('YYYY-MM-DD');
        const dayBookings = bookings.filter(b => moment(b.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr &&
            isBookingOccupyingSlot(b));
        const dayBlocked = blockedSlots.filter(s => moment(s.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr);

        const bookedIds = new Set(dayBookings.map(b => b.slot_id));
        const blockedIds = new Set(dayBlocked.map(s => s.slot_id));

        const total = 6;
        const available = total - bookedIds.size - blockedIds.size;

        setDayStats({
            totalSlots: total,
            available: Math.max(0, available),
            booked: bookedIds.size,
            blocked: blockedIds.size
        });
    };

    const getSlotStatus = (date, slotId) => {
        const dateStr = date.format('YYYY-MM-DD');
        const booking = bookings.find(b => moment(b.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr && b.slot_id === slotId && isBookingOccupyingSlot(b));
        const blocked = blockedSlots.find(s => moment(s.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr && s.slot_id === slotId);

        if (booking) return 'booked';
        if (blocked) return 'blocked';
        return 'available';
    };

    const handlePrevMonth = () => setCurrentDate(moment(currentDate).subtract(1, 'month'));
    const handleNextMonth = () => setCurrentDate(moment(currentDate).add(1, 'month'));
    const handleToday = () => {
        const today = moment();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    // Actions
    const handleBlockSlot = async (slotId) => {
        if (!window.confirm(`Block slot ${slotId} on ${selectedDate.format('MMM DD')}?`)) return;
        setLoadingAction(true);
        try {
            const token = localStorage.getItem('admin_token');
            const payload = {
                type: 'slot',
                date: selectedDate.format('YYYY-MM-DD'),
                slot_id: slotId,
                reason: 'Manual block from calendar'
            };
            await axios.post(`${API_BASE_URL}/admin/block-slot`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Slot blocked');
            fetchMonthData();
        } catch (error) {
            toast.error('Failed to block');
        } finally {
            setLoadingAction(false);
        }
    };

    const handleUnblockSlot = async (id) => {
        if (!window.confirm('Unblock this slot?')) return;
        setLoadingAction(true);
        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(`${API_BASE_URL}/admin/block-slot/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Slot unblocked');
            fetchMonthData();
        } catch (error) {
            toast.error('Failed to unblock');
        } finally {
            setLoadingAction(false);
        }
    };

    const handleDeleteBooking = async (id) => {
        if (!window.confirm('Delete this booking permanently?')) return;
        setLoadingAction(true);
        try {
            const token = localStorage.getItem('admin_token');
            await axios.delete(`${API_BASE_URL}/admin/bookings/${id}/hard`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Booking deleted');
            fetchMonthData();
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setLoadingAction(false);
        }
    };

    const getDayStatus = (date) => {
        const dateStr = date.format('YYYY-MM-DD');
        const dayBookings = bookings.filter(b => moment(b.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr &&
            isBookingOccupyingSlot(b));
        const dayBlocked = blockedSlots.filter(s => moment(s.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr);

        const totalSlots = 6;
        const bookingsCount = new Set(dayBookings.map(b => b.slot_id)).size;
        const blockedCount = new Set(dayBlocked.map(s => s.slot_id)).size;
        const available = Math.max(0, totalSlots - bookingsCount - blockedCount);

        // Fully blocked (all slots blocked, no bookings)
        if (blockedCount === totalSlots) {
            return { status: 'blocked', label: 'Blocked', color: 'blue' };
        }

        // Fully booked (all slots taken by bookings or blocked)
        if (available === 0) {
            return { status: 'full', label: 'Full', color: 'red' };
        }

        // All available (no bookings, no blocked slots)
        if (bookingsCount === 0 && blockedCount === 0) {
            return { status: 'available', label: 'All Free', color: 'green' };
        }

        // Partial (some slots booked/blocked, some available)
        return { status: 'partial', label: `${available} Free`, color: 'yellow' };
    };

    // Render calendar grid
    const renderCalendar = () => {
        const startDay = moment(currentDate).startOf('month').startOf('week');
        const endDay = moment(currentDate).endOf('month').endOf('week');
        const calendar = [];
        let day = startDay.clone();

        while (day.isBefore(endDay, 'day')) {
            calendar.push(
                Array(7).fill(0).map(() => {
                    const dayClone = day.clone();
                    const dayStatus = getDayStatus(dayClone);
                    const isSelected = selectedDate.isSame(dayClone, 'day');
                    const isCurrentMonth = dayClone.isSame(currentDate, 'month');
                    const isToday = dayClone.isSame(moment(), 'day');

                    // Calculate slot statuses for this day to show mini-dots if needed, 
                    // but primarily relying on the card background/text now.

                    day.add(1, 'day');

                    // Base classes
                    let cellClasses = `min-h-[120px] p-3 cursor-pointer transition-all relative border-b border-r border-gray-100 flex flex-col justify-between group
                        ${!isCurrentMonth ? 'bg-gray-50/50 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-800'}
                    `;

                    // Status based background styling (softer)
                    if (isCurrentMonth) {
                        if (dayStatus.status === 'full') cellClasses += ' hover:bg-red-50/30';
                        if (dayStatus.status === 'available') cellClasses += ' hover:bg-green-50/30';
                        if (dayStatus.status === 'blocked') cellClasses += ' bg-gray-50 hover:bg-gray-100';
                    }

                    // Selected state overrides
                    if (isSelected) {
                        cellClasses += ' ring-2 ring-inset ring-blue-500 bg-blue-50/20 z-10';
                    }

                    return (
                        <div
                            key={dayClone.toString()}
                            onClick={() => setSelectedDate(dayClone)}
                            className={cellClasses}
                        >
                            <div className="flex justify-between items-start">
                                <span className={`text-base font-semibold w-8 h-8 flex items-center justify-center rounded-full transition-colors
                                    ${isToday
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}
                                `}>
                                    {dayClone.date()}
                                </span>
                            </div>

                            {/* Clear Text Label Status */}
                            {isCurrentMonth && (
                                <div className="mt-2 text-center">
                                    <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold border
                                        ${dayStatus.status === 'available' ? 'bg-green-100 text-green-700 border-green-200' :
                                            dayStatus.status === 'full' ? 'bg-red-100 text-red-700 border-red-200' :
                                                dayStatus.status === 'blocked' ? 'bg-gray-100 text-gray-600 border-gray-200' :
                                                    'bg-amber-100 text-amber-700 border-amber-200'}
                                    `}>
                                        {dayStatus.label}
                                    </span>
                                </div>
                            )}

                            {/* Visual Dots for specific slots */}
                            {isCurrentMonth && dayStatus.status !== 'available' && (
                                <div className="flex gap-0.5 justify-center mt-2 opacity-50">
                                    {slots.map((s, i) => {
                                        const st = getSlotStatus(dayClone, s.id);
                                        return (
                                            <div key={i} className={`w-1.5 h-1.5 rounded-full ${st === 'booked' ? 'bg-red-400' :
                                                st === 'blocked' ? 'bg-blue-400' : 'bg-gray-200'
                                                }`} />
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })
            );
        }
        return calendar;
    };

    // Modal State
    const [selectedBooking, setSelectedBooking] = useState(null);

    if (loadError) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-10 text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-800 mb-1">Couldn’t load calendar data</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                    Availability is hidden rather than shown as free — every slot would look open,
                    and booking against that could double-book a date.
                </p>
                <button
                    onClick={fetchMonthData}
                    className="mt-5 px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-100px)] gap-6 antialiased bg-gray-50/50 p-6 rounded-2xl relative">
            {/* Calendar Area */}
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white">
                    <div className="flex items-center gap-6">
                        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                            <CalendarIcon className="w-6 h-6 text-blue-600" />
                            {currentDate.format('MMMM YYYY')}
                        </h2>
                        <div className="flex items-center bg-gray-100 p-1 rounded-lg">
                            <button onClick={handlePrevMonth} className="p-1.5 hover:bg-white rounded-md shadow-sm text-gray-600 transition-all"><ChevronLeft className="w-5 h-5" /></button>
                            <button onClick={handleToday} className="px-4 py-1 text-sm font-semibold text-gray-700 hover:bg-white rounded-md shadow-sm transition-all mx-1">Today</button>
                            <button onClick={handleNextMonth} className="p-1.5 hover:bg-white rounded-md shadow-sm text-gray-600 transition-all"><ChevronRight className="w-5 h-5" /></button>
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="flex gap-6 text-sm font-medium text-gray-600 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-green-400 ring-2 ring-green-100"></div> Available</div>
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-400 ring-2 ring-red-100"></div> Booked</div>
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-blue-400 ring-2 ring-blue-100"></div> Blocked</div>
                    </div>
                </div>

                {/* Days Header */}
                <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50/80 backdrop-blur-sm sticky top-0 z-20">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                        <div key={d} className="py-4 text-center text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {d}
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {renderCalendar().map((week, i) => (
                        <div key={i} className="grid grid-cols-7">
                            {week}
                        </div>
                    ))}
                </div>
            </div>

            {/* Side Panel - Details */}
            <div className="w-[400px] bg-white rounded-2xl shadow-xl border border-gray-200 flex flex-col overflow-hidden transform transition-all duration-300">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-baseline justify-between mb-1">
                        <h3 className="text-2xl font-bold text-gray-900">
                            {selectedDate.format('MMM DD')}
                        </h3>
                        <span className="text-gray-400 font-medium">{selectedDate.format('dddd')}</span>
                    </div>

                    {/* Quick Stats Cards */}
                    <div className="mt-6 grid grid-cols-3 gap-3">
                        <div className="bg-green-50 p-3 rounded-xl border border-green-100 text-center transform hover:scale-105 transition-transform duration-200">
                            <div className="text-2xl font-bold text-green-600">{dayStats.available}</div>
                            <div className="text-[10px] font-bold text-green-800/60 uppercase tracking-widest mt-1">Free</div>
                        </div>
                        <div className="bg-red-50 p-3 rounded-xl border border-red-100 text-center transform hover:scale-105 transition-transform duration-200">
                            <div className="text-2xl font-bold text-red-600">{dayStats.booked}</div>
                            <div className="text-[10px] font-bold text-red-800/60 uppercase tracking-widest mt-1">Booked</div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 text-center transform hover:scale-105 transition-transform duration-200">
                            <div className="text-2xl font-bold text-blue-600">{dayStats.blocked}</div>
                            <div className="text-[10px] font-bold text-blue-800/60 uppercase tracking-widest mt-1">Blocked</div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                    {slots.map((slot, idx) => {
                        const dateStr = selectedDate.format('YYYY-MM-DD');
                        const booking = bookings.find(b => moment(b.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr && b.slot_id === slot.id && isBookingOccupyingSlot(b));
                        const blocked = blockedSlots.find(s => moment(s.slot_time).utcOffset(330).format('YYYY-MM-DD') === dateStr && s.slot_id === slot.id);

                        let status = 'available';
                        if (booking) status = 'booked';
                        if (blocked) status = 'blocked';

                        return (
                            <div key={idx} className="group relative">
                                {/* Timeline Connector */}
                                {idx !== slots.length - 1 && (
                                    <div className="absolute left-[19px] top-10 bottom-[-14px] w-0.5 bg-gray-100 group-hover:bg-gray-200 transition-colors"></div>
                                )}

                                <div className={`relative flex gap-4 p-4 rounded-xl border transition-all duration-200
                                    ${status === 'available' ? 'bg-white border-gray-100 hover:border-green-200 hover:shadow-md hover:shadow-green-50' :
                                        status === 'booked' ? 'bg-red-50/30 border-red-100 hover:border-red-200' :
                                            'bg-blue-50/30 border-blue-100 hover:border-blue-200'}
                                `}>
                                    {/* Slot Icon/Badge */}
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 z-10 bg-white
                                        ${status === 'available' ? 'border-green-200 text-green-600' :
                                            status === 'booked' ? 'border-red-200 text-red-600' :
                                                'border-blue-200 text-blue-600'}
                                    `}>
                                        <span className="text-xs font-bold">{slot.id}</span>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-800">{slot.label}</h4>
                                                <span className="text-xs text-gray-400 font-medium">{slot.duration} • {slot.type}</span>
                                            </div>

                                            {/* Action Buttons */}
                                            {status === 'available' && (
                                                <button
                                                    onClick={() => handleBlockSlot(slot.id)}
                                                    disabled={loadingAction}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                                    title="Block Slot"
                                                >
                                                    <Shield className="w-4 h-4" />
                                                </button>
                                            )}
                                            {status === 'blocked' && (
                                                <button
                                                    onClick={() => handleUnblockSlot(blocked.id)}
                                                    disabled={loadingAction}
                                                    className="p-1.5 text-blue-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Unblock Slot"
                                                >
                                                    <Unlock className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        {status === 'booked' && (
                                            <div className="mt-3 bg-white p-3 rounded-lg border border-red-100 shadow-sm cursor-pointer hover:bg-red-50/50 transition-colors"
                                                onClick={() => setSelectedBooking(booking)}
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                                        <User className="w-3.5 h-3.5" />
                                                    </div>
                                                    <span className="text-sm font-semibold text-gray-800 truncate">{booking.user_name}</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase
                                                            ${booking.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                                                    `}>{booking.payment_status}</span>
                                                    <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                                                        View Details <MoreHorizontal className="w-3 h-3" />
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {status === 'blocked' && (
                                            <div className="mt-2 text-xs text-blue-800/70 bg-blue-50/50 p-2 rounded-lg border border-blue-100/50">
                                                <span className="font-semibold">Reason:</span> {blocked.reason || 'Manual block'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal */}
            <BookingDetailsModal
                booking={selectedBooking}
                onClose={() => setSelectedBooking(null)}
                onUpdated={fetchMonthData}
            />
        </div>
    );
};

export default CalendarManager;
